/**
 * The notes dropdown in the editor's top bar — §2.8 #64.
 *
 * Extracted from `NotesViewer.tsx`. Replaces what used to be a left sidebar,
 * which is why it carries the note count and the preview text: the list is no
 * longer on screen, so the trigger has to say how much is behind it.
 *
 * Below `sm` the list opens as a bottom sheet rather than a 320px dropdown,
 * which on a phone hung off the screen's edge; the rows are the same.
 *
 * Fully controlled except for its own open/closed state. Selection lives in
 * `useNoteEditorBuffer` one level up, because selecting a note also swaps the
 * editor buffer and those two writes must not be able to disagree.
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, FileEdit, Trash2 } from 'lucide-react';
import { Sheet } from '@ai-tutor/ui';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';
import { formatNoteDate, getNotePreview } from '@/lib/notes/preview';
import type { NoteItem } from '@/lib/api-client';

export interface NoteSelectorProps {
  notes: NoteItem[];
  selectedNoteId: string | null;
  onSelect: (note: NoteItem) => void;
  onDelete: (noteId: string, e: React.MouseEvent) => void;
}

export function NoteSelector({
  notes,
  selectedNoteId,
  onSelect,
  onDelete,
}: NoteSelectorProps) {
  const [open, setOpen] = useState(false);
  const phone = useMediaQuery('(max-width: 639.98px)');
  const ref = useRef<HTMLDivElement>(null);
  const selectedNote = notes.find(n => n.note_id === selectedNoteId) ?? null;

  // Outside click closes. Bound on `mousedown` rather than `click` so the menu
  // is gone before the click lands on whatever is underneath — with `click`,
  // dismissing the menu by pressing a button behind it both closes the menu and
  // fires that button, which reads as a misclick.
  useEffect(() => {
    if (!open || phone) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, phone]);

  const list = (
    <NoteList
      notes={notes}
      selectedNoteId={selectedNoteId}
      onSelect={note => {
        onSelect(note);
        setOpen(false);
      }}
      onDelete={onDelete}
    />
  );

  return (
    <div className='relative' ref={ref}>
      <button
        type='button'
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className='inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-muted-foreground transition-all duration-200 hover:bg-muted hover:text-foreground max-sm:min-h-11'
      >
        <FileEdit
          className='h-4 w-4 text-muted-foreground'
          aria-hidden='true'
        />
        <span className='max-w-[220px] truncate max-sm:max-w-[140px]'>
          {selectedNote?.title || 'Untitled'}
        </span>
        {/* The bare count next to a title reads as part of the title to a
            screen reader ("Photosynthesis 3"), so it carries its own label. */}
        <span
          className='rounded-sm bg-muted px-1.5 py-0.5 text-xs text-muted-foreground'
          aria-label={`${notes.length} ${notes.length === 1 ? 'note' : 'notes'}`}
        >
          {notes.length}
        </span>
        <ChevronDown
          aria-hidden='true'
          className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {phone ? (
        <Sheet
          isOpen={open}
          onClose={() => setOpen(false)}
          title='Your notes'
          size='auto'
        >
          <div className='pb-[env(safe-area-inset-bottom)]'>{list}</div>
        </Sheet>
      ) : (
        open && (
          <div className='absolute left-0 top-full z-50 mt-1 w-80 overflow-hidden rounded-lg border border-border bg-card shadow-lg'>
            <div className='max-h-80 overflow-y-auto'>{list}</div>
          </div>
        )
      )}
    </div>
  );
}

function NoteList({
  notes,
  selectedNoteId,
  onSelect,
  onDelete,
}: NoteSelectorProps) {
  return (
    // `group`, not `menu` / `listbox` — see the note in `SlashMenu`. Each row
    // here is TWO controls (open, delete), which no menu-ish role models, and
    // the rows sit inside a scroll wrapper that axe rejects as a menu child.
    <div role='group' aria-label='Notes' className='py-1'>
      {notes.map(note => {
        const isSelected = selectedNoteId === note.note_id;
        return (
          <div
            key={note.note_id}
            className={`group flex items-start gap-2 px-3 py-2 transition-colors max-sm:items-center max-sm:px-4 max-sm:py-2.5 ${
              isSelected
                ? 'bg-brand-50/70 dark:bg-tint-brand'
                : 'hover:bg-muted'
            }`}
          >
            <button
              type='button'
              // Same reasoning as the slash menu: the open note is marked by a
              // background tint, which conveys nothing non-visually.
              aria-current={isSelected}
              onClick={() => onSelect(note)}
              className='flex-1 overflow-hidden text-left max-sm:min-h-11'
            >
              <div className='flex items-center justify-between gap-2'>
                <span
                  className={`truncate text-sm font-medium ${
                    isSelected
                      ? 'text-brand-700 dark:text-tint-brand-foreground'
                      : 'text-foreground'
                  }`}
                >
                  {note.title || 'Untitled'}
                </span>
                <span className='flex-shrink-0 text-xs text-muted-foreground'>
                  {formatNoteDate(note.updated_at)}
                </span>
              </div>
              <p className='mt-0.5 truncate text-xs text-muted-foreground'>
                {getNotePreview(note.content)}
              </p>
            </button>
            {/* Hover-revealed with a mouse, but `opacity-0` hides it from
                sight only, not from the tab order - a keyboard user has no
                hover, so it stays reachable and carries a real name. On a
                phone or touch screen there is no hover at all, so it is
                always shown, with a 44px tap area. */}
            <button
              type='button'
              onClick={e => onDelete(note.note_id, e)}
              className='tap-target flex-shrink-0 rounded-sm p-1 text-muted-foreground opacity-0 transition-all hover:bg-red-50 hover:text-red-500 focus-visible:opacity-100 group-hover:opacity-100 dark:hover:bg-red-900/20 max-sm:opacity-100 [@media(pointer:coarse)]:opacity-100'
              aria-label={`Delete ${note.title || 'Untitled'}`}
              title='Delete note'
            >
              <Trash2 className='h-3.5 w-3.5' aria-hidden='true' />
            </button>
          </div>
        );
      })}
    </div>
  );
}
